import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Form } from '@unform/web';
import { useDispatch, useSelector } from 'react-redux';
import { subDays, addDays, format, getDate } from 'date-fns';
import pt from 'date-fns/locale/pt';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { downloadFile } from 'utils';
import api from 'services/api';
import { updateCurrentDate } from 'store/modules/usuario/actions';
import { show } from 'store/modules/dialog/actions';
import AppError from 'utils/AppError';
import midiainfo from 'utils/midiainfo';

import Input from 'components/Input';
import Card from 'components/Card';
import SelectDate from 'components/SelectDate';
import Tooltip from 'components/Tooltip';
import Switch from 'components/Switch/withoutForm';
import Flag from 'components/Flag';
import DropDownMenu from 'components/DropDownMenu';
import Loader from 'components/ContentLoader/ComerciaisGravadosLoader';
import PlayerBaixa from 'components/PlayerBaixa';
import Button from 'components/Button';
import NotFound from 'components/NotFound';
import Avatar from 'components/Avatar';

import Gravados from 'assets/gravados.svg';

import {
  FiDownload,
  FiSearch,
  FiPlay,
  FiRefreshCw,
  FiActivity,
  FiInfo,
  FiMessageSquare,
  FiFileText,
  FiArrowLeft,
} from 'react-icons/fi';

import { Scroll, Table } from 'styles/global';
import { Td, TdFlag } from 'pages/Comercial/styles';
import { Container } from './styles';

import {
  Comercial as IComercial,
  Midia as IMidia,
  EnviaExibidor as IEnviaExibidor,
  Usuario,
  Emissora,
} from 'pages/@types';
import { Role, StatusConferencia, StatusPreAvaliacao } from 'utils/variables';

interface PlanilhaGravacao {
  planilha_url: string;
  originalfilename: string;
}

interface Midia extends IMidia {
  usuario?: { nome: string; avatar_url: string };
  statuspreavaliacao16ch: string;
}

interface Comercial extends IComercial {
  midia: Midia;
}
interface EnviaExibidor extends IEnviaExibidor {
  loading: boolean;
  emissoraorigem: Emissora;
  roteiro?: {
    abrangencia: string;
    codmaterial: number;
    ippt: string;
    numippt: number;
    roteiro: { datautilizacao: string };
    programa: { nome: string };
  };
  comercial: Comercial;
  usuario: Usuario;
}

export default function ComerciaisGravados() {
  // react-router-dom
  const navigate = useNavigate();
  /**
   * REDUX
   * */
  const dispatch = useDispatch();
  const { emissora, currentDate, profile } = useSelector(
    (state: any) => state.usuario,
  );
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );
  /*
   * VARIAVEIS
   */

  const [selected, setSelected] = useState<EnviaExibidor | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [loadingRoteiroConferencia, setLoadingRoteiroConferencia] = useState(
    false,
  );
  const [gravadosCompleto, setGravadosCompleto] = useState<EnviaExibidor[]>([]);
  const [filtro, setFiltro] = useState('');
  const [ultimaPlanilha, setUltimaPlanilha] = useState<PlanilhaGravacao | null>(
    null,
  );

  const [player, setPlayer] = useState({
    open: false,
    midia_url: '',
    pipe: false,
  });

  const gravados = gravadosCompleto.filter(
    gra =>
      gra.comercial.titulo.includes(filtro.toUpperCase()) ||
      gra.comercial.codmaterial.toString().includes(filtro) ||
      gra.comercial.cliente.mnemonico.includes(filtro.toUpperCase()),
  );
  /**
   * USE MEMO
   */
  const dateFormatted = useMemo(() => {
    if (currentDate) {
      return format(new Date(currentDate), "d 'de' MMMM", { locale: pt });
    } else return format(new Date(), "d 'de' MMMM", { locale: pt });
  }, [currentDate]);

  /**
   * FUNÇÕES
   */
  const handlePrevDay = useCallback(() => {
    dispatch(updateCurrentDate(subDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleNextDay = useCallback(() => {
    dispatch(updateCurrentDate(addDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleDownloadPlanilha = useCallback(async () => {
    if (ultimaPlanilha) {
      setLoadingDownload(true);
      try {
        await downloadFile({
          url: ultimaPlanilha.planilha_url,
          name: ultimaPlanilha.originalfilename,
        });
      } catch (error) {
        toast.error(
          'Ocorreu um erro ao tentar baixar a planilha, tente novamente mais tarde',
        );
      } finally {
        setLoadingDownload(false);
      }
    }
  }, [ultimaPlanilha]);

  const handleDateChange = useCallback(
    date => {
      date && dispatch(updateCurrentDate(date));
    },
    [dispatch],
  );
  const handleSwitchChange = useCallback(
    async (e, gra) => {
      setGravadosCompleto(prevState =>
        prevState.map(g =>
          g.id === gra.id ? { ...g, loading: true } : { ...g },
        ),
      );

      const statusconferencia = e.target.checked
        ? StatusConferencia.APROVADO
        : StatusConferencia.PENDENTE;

      try {
        const { data } = await api.patch(`/enviaexibidor/${gra.id}`, {
          statusconferencia,
        });

        setGravadosCompleto(prevState =>
          prevState.map(c =>
            c.id === gra.id
              ? {
                  ...c,
                  loading: false,
                  statusgeral: data.statusgeral,
                  statusconferencia: data.statusconferencia,
                  usuario: profile,
                }
              : c,
          ),
        );

        toast.success(`Alterado conferência de ${gra.comercial.titulo}`);
      } catch (error) {
        setGravadosCompleto(prevState =>
          prevState.map(c =>
            c.id === gra.id
              ? {
                  ...c,
                  loading: false,
                }
              : c,
          ),
        );
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
    },
    [profile],
  );
  const handleGeraRoteiroConferencia = useCallback(async () => {
    try {
      setLoadingRoteiroConferencia(true);
      await api.post('/roteiroconferencia', {
        emissora_id: emissora.value,
        data: format(new Date(currentDate), 'yyyy-MM-dd'),
      });
      toast.success('Gerado Roteiro de Conferência');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        const appError = error as AppError;
        toast.error(appError.message);
      }
    } finally {
      setLoadingRoteiroConferencia(false);
    }
  }, [currentDate, emissora.value]);

  const handleMidiaInfoDialog = useCallback(
    async id => {
      const response = await api.get(`/midias/${id}`);
      if (response && response.data && response.data.pendente) {
        const comercial = response.data.pendente;
        if (comercial && comercial.midia && comercial.midia.midiainfo) {
          if (
            comercial.midia.midiainfo8ch &&
            comercial.midia.statuspreavaliacao8ch
          ) {
            const options = midiainfo(
              comercial.midia.statuspreavaliacao8ch,
              comercial.midia.midiainfo8ch,
            );
            dispatch(show(options));
          }
        }
      } else toast.error('Falha de comunicação com o sistema');
    },
    [dispatch],
  );

  const handleRessalva = useCallback(
    gra => {
      const contentHTML = `<p>Observação: ${gra.comercial.midia.obseng}</p>`;
      const options = {
        type: 'info',
        contentHTML,
        confirmButtonText: 'FECHAR',
        showCancelButton: false,
        showConfirmButton: true,
      };
      dispatch(show(options));
    },
    [dispatch],
  );

  const handlePlayer = useCallback(
    midia => {
      if (!!midia && midia.preview_url) {
        setPlayer({
          open: !player.open,
          midia_url: midia.preview_url,
          pipe: false,
        });
      } else {
        dispatch(
          show({
            type: 'warning',
            text: 'Preview indisponível no momento',
            showConfirmButton: false,
          }),
        );
      }
    },
    [dispatch, player.open],
  );

  function handleActionSelect(
    c: EnviaExibidor,
    action: { value: number; label: string },
  ) {
    const optionBase = {
      type: 'question',

      confirmButtonText: 'CONFIRMAR',
      callback: {
        url: `/enviaexibidor/${c.id}`,
        method: api.patch,
        data: { status: 'ingestmanual' },
      },
      haveCallback: true,
      successMessage: 'Status alterado!',
      cancelButtonText: 'NÃO',
      showCancelButton: false,
      showConfirmButton: true,
      showCloseButton: true,
    };

    let options = {};

    switch (action.value) {
      // case 1: // INGEST MANUAL
      //   options = {
      //     ...optionBase,
      //     contentHTML: `<p>Alterar status de envio do material <b>${c.comercial.titulo}</b> para <b>${action.label}</b> ?</p>`,
      //     callback: {
      //       ...optionBase.callback,
      //     },
      //   };
      //   setSelected({
      //     ...c,
      //     statusgeral: {
      //       order: 17,
      //       nome: 'INGEST MANUAL',
      //       type: 'ingest_manual',
      //       info: 'Realizada gravação manual no exibidor',
      //     },
      //   });
      //   dispatch(show(options));
      //   break;
      case 1:
        options = {
          ...optionBase,
          contentHTML: `<p>Iniciar um novo envio do material <b>${c.comercial.titulo}</b> para o exibidor ?</p>`,
          callback: {
            ...optionBase.callback,
            action: 'reenvio',
            data: { status: 'pendente' },
          },
        };
        setSelected({
          ...c,
          statusgeral: {
            order: 16,
            nome: 'FILA ENVIO EXIBIDOR',
            type: 'fila_envio',
            info: 'Midia na fila para envio ao exibidor',
          },
        });
        dispatch(show(options));
        break;

      case 2:
        handlePlayer(c.comercial.midia);
        break;
      // case 5:
      //   setSelected(c);
      //   options = {
      //     ...optionBase,
      //     contentHTML: `Arquivar ${c.comercial.titulo} ?`,
      //     callback: {
      //       url: `/enviaexibidor/${c.id}`,
      //       method: api.delete,
      //     },
      //   };
      //   dispatch(show(options));
      //   break;
      case 3:
        handleMidiaInfoDialog(c.comercial);
        break;

      default:
        break;
    }
  }
  // fecha player
  const closeVideo = useCallback(() => {
    setPlayer({
      open: !player.open,
      midia_url: '',
      pipe: false,
    });
  }, [player.open]);
  /**
   * USE EFFECT
   */
  useEffect(() => {
    const controller = new AbortController();
    async function loadGravados() {
      setLoading(true);
      try {
        const response = await api.get(`/dashboard/comerciaisgravados`, {
          signal: controller.signal,
          params: {
            emissora_id: emissora.value,
            date: format(new Date(currentDate), 'yyyy-MM-dd'),
          },
        });
        if (response && response.data) {
          setGravadosCompleto(response.data);
          setUltimaPlanilha(response.data.ultimaPlanilha);
        }
        setLoading(false);
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
    }
    loadGravados();

    return () => {
      controller.abort();
    };
  }, [currentDate, emissora.value]);

  // verifica se callback foi realizado com sucesso para então remover item da lista
  useEffect(() => {
    if (callbackDialog && callbackDialog.success)
      if (callbackDialog.success.action === 'success' && selected) {
        setGravadosCompleto(prevState =>
          prevState.map(gra =>
            gra.id === selected.id
              ? { ...gra, statusgeral: selected.statusgeral }
              : { ...gra },
          ),
        );
        setSelected(null);
      }
  }, [callbackDialog, selected]);

  return (
    <Container>
      <nav>
        <Button size="lg" color="primary" onClick={() => navigate(-1)}>
          VOLTAR
          <FiArrowLeft />
        </Button>
        <SelectDate
          dateFormatted={dateFormatted}
          currentDateZoned={format(
            addDays(new Date(currentDate), 1),
            'yyyy-MM-dd',
          )}
          handleNextDay={handleNextDay}
          handleDateChange={handleDateChange}
          handlePrevDay={handlePrevDay}
          disabledNextDay={
            getDate(new Date(currentDate)) === getDate(new Date())
          }
        />
        <Button
          color="primary"
          onClick={() => handleGeraRoteiroConferencia()}
          disabled={
            loadingRoteiroConferencia ||
            ![Role.ADMIN, Role.ENG].includes(profile.role)
          }
        >
          {loadingRoteiroConferencia ? (
            'Preparando arquivo'
          ) : (
            <>
              <FiFileText /> Roteiro de conferência
            </>
          )}
        </Button>
      </nav>
      <Card>
        <header>
          <h4>COMERCIAIS GRAVADOS</h4>

          <div>
            <Form onSubmit={() => {}}>
              <Input
                name="searchLocal"
                placeholder="Buscar na Tabela"
                icon={<FiSearch />}
                onChange={e => setFiltro(e.target.value)}
              />
            </Form>
          </div>
        </header>
        {loading ? (
          <Loader />
        ) : gravados && gravados.length > 0 ? (
          <Scroll>
            <Table>
              <thead>
                <tr>
                  <th />
                  <th style={{ width: '4rem' }}>CM</th>
                  <th>TITULO</th>
                  <th style={{ width: '4rem', textAlign: 'center' }}>DUR.</th>
                  <th style={{ width: '4rem', textAlign: 'center' }}>CC</th>
                  <th style={{ width: '10rem', textAlign: 'center' }}>
                    STATUS
                  </th>
                  <th style={{ width: '4rem' }}>AVAL.</th>
                  <th style={{ width: '2rem' }}>OBS.</th>
                  <th style={{ width: '8rem', textAlign: 'center' }}>
                    CONFERIDO
                  </th>
                  <th style={{ width: '4rem' }}>POR</th>
                  <th style={{ width: '4rem' }}>ORIGEM</th>
                  <th style={{ width: '2rem' }} />
                </tr>
              </thead>

              <tbody>
                {gravados.map((gra, index) => (
                  <tr key={gra.id}>
                    <TdFlag $isGe={gra.comercial.globoexpress}>
                      <div id={`tdFlag_id${index}`} />
                      <Tooltip target={`tdFlag_id${index}`} position="right">
                        <p>
                          <strong>PRIORITÁRIO:</strong> GLOBO EXPRESS
                        </p>
                      </Tooltip>
                    </TdFlag>
                    <Td disabled={gra.deleted_at !== null}>
                      {gra.comercial.codmaterial}
                    </Td>
                    <Td disabled={gra.deleted_at !== null}>
                      {gra.comercial.titulo}{' '}
                      <strong>({gra.comercial.cliente.mnemonico})</strong>
                    </Td>
                    <Td
                      style={{ textAlign: 'center' }}
                      disabled={gra.deleted_at !== null}
                    >
                      {gra.comercial.duracao} s
                    </Td>
                    <Td>
                      <Flag
                        disabled={gra.deleted_at !== null}
                        type={
                          !!gra.comercial.midia &&
                          gra.comercial.midia.closedcaption
                            ? 'closedcaption'
                            : 'noclosedcaption'
                        }
                        name={
                          !!gra.comercial.midia &&
                          gra.comercial.midia.closedcaption
                            ? 'SIM'
                            : 'NÃO'
                        }
                      />
                    </Td>
                    <Td>
                      <Flag
                        disabled={gra.deleted_at !== null}
                        type={gra.statusgeral.type}
                        name={gra.statusgeral.nome}
                        tooltip={gra.statusgeral.info}
                      />
                    </Td>
                    <Td disabled={gra.deleted_at !== null}>
                      {gra.comercial &&
                      gra.comercial.midia &&
                      gra.comercial.midia.avaliador ? (
                        <Avatar
                          size="40"
                          name={gra.comercial.midia.avaliador.nome}
                          src={gra.comercial.midia.avaliador.avatar_url}
                          round
                        />
                      ) : (
                        <p>...</p>
                      )}
                    </Td>
                    <Td>
                      {gra.comercial &&
                        gra.comercial.midia &&
                        gra.comercial.midia.obseng && (
                          <Button
                            fill
                            color="font"
                            round
                            onClick={() => handleRessalva(gra)}
                          >
                            <FiMessageSquare />
                          </Button>
                        )}
                    </Td>
                    <Td>
                      <Switch
                        disabled={gra.deleted_at !== null}
                        id={gra.id}
                        loading={gra.loading}
                        onChange={e => handleSwitchChange(e, gra)}
                        checked={
                          gra.statusconferencia === StatusConferencia.APROVADO
                        }
                      />
                    </Td>
                    <Td>
                      {gra.usuarioconferencia && gra.usuarioconferencia.nome ? (
                        <Avatar
                          size="40"
                          name={gra.usuarioconferencia.nome}
                          src={gra.usuarioconferencia.avatar_url}
                          round
                        />
                      ) : (
                        <p>...</p>
                      )}
                    </Td>
                    <Td>{gra.emissoraorigem && gra.emissoraorigem.sigla}</Td>
                    <Td>
                      <DropDownMenu
                        disabled={false}
                        id={`acoes${index}`}
                        actions={[
                          {
                            value: 0,
                            label: 'HISTÓRICO',
                            icon: <FiActivity />,
                            href: `/historico/${gra.comercial.id}`,
                          },
                          {
                            value: 1,
                            label: 'REENVIAR',
                            icon: <FiRefreshCw />,
                            disabled:
                              gra.deleted_at !== null ||
                              (gra.statusgeral.type !== 'gravado' &&
                                gra.statusgeral.type !== 'conferido' &&
                                gra.statusgeral.type !== 'ingest_manual'),
                          },
                          {
                            value: 2,
                            label: 'ASSISTIR',
                            icon: <FiPlay />,
                            disabled:
                              gra.deleted_at !== null ||
                              !gra.comercial ||
                              !gra.comercial.midia ||
                              !gra.comercial.midia.preview_url,
                          },
                          {
                            value: 3,
                            label: 'MIDIAINFO',
                            icon: <FiInfo />,
                            disabled:
                              gra.deleted_at !== null ||
                              !gra.comercial ||
                              !gra.comercial.midia ||
                              (gra.comercial.midia.statuspreavaliacao16ch !==
                                StatusPreAvaliacao.APROVADO &&
                                gra.comercial.midia.statuspreavaliacao16ch !==
                                  StatusPreAvaliacao.REPROVADO),
                          },
                        ]}
                        handleClick={action =>
                          !action.disabled && handleActionSelect(gra, action)
                        }
                      />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroll>
        ) : (
          <div>
            <NotFound img={Gravados} title="COMERCIAIS GRAVADOS">
              Nenhum comercial gravado encontrado
            </NotFound>
          </div>
        )}
      </Card>
      <PlayerBaixa
        open={player.open}
        toggleModal={() => closeVideo()}
        pipe={player.pipe}
        url={player.midia_url}
      />
    </Container>
  );
}
